import { tijdstip } from './datum.js';
import { collator, label, norm, sorteerbaar, vindKolom } from './kolom.js';
import type { Context, Kolom, Lijst, Richting, Sortering } from './typen.js';

type Vergelijker<R> = (a: R, b: R) => number;

function vergelijker<R, C extends Context>(k: Kolom<R, C>, richting: Richting, ctx: C): Vergelijker<R> {
  const sleutel = (r: R): string | number | null => {
    const w = norm(k.waarde(r, ctx));
    if (w === null) return null;
    if (k.type === 'getal') return Number.isNaN(Number(w)) ? null : Number(w);
    if (k.type === 'datum') return tijdstip(w);
    return k.type === 'keuzelijst' ? label(k, w, ctx) : w;
  };
  const teken = richting === 'af' ? -1 : 1;
  return (a, b) => {
    const x = sleutel(a);
    const y = sleutel(b);
    // Leeg staat per kolom altijd onderaan, in beide richtingen.
    if (x === null || y === null) return Number(x === null) - Number(y === null);
    const v = typeof x === 'number' && typeof y === 'number' ? x - y : collator.compare(String(x), String(y));
    return v * teken;
  };
}

/** Stapelsortering; daarna de standaardsortering, daarna de oorspronkelijke volgorde. `vastOnderaan` blijft onderaan. */
export function sorteer<R, C extends Context>(rijen: readonly R[], lijst: Lijst<R, C>, sortering: Sortering, ctx: C): R[] {
  const vergelijkers = [...sortering, ...(lijst.standaardSortering?.(ctx) ?? [])].flatMap(({ sleutel, richting }) => {
    const k = vindKolom(lijst, sleutel);
    return k && sorteerbaar(k) && (k.zichtbaar?.(ctx) ?? true) ? [vergelijker(k, richting, ctx)] : [];
  });
  const onderaan = (r: R) => Number(lijst.vastOnderaan?.(r) ?? false);
  return rijen
    .map((rij, i) => ({ rij, i }))
    .sort((a, b) => {
      const vast = onderaan(a.rij) - onderaan(b.rij);
      if (vast !== 0) return vast;
      for (const v of vergelijkers) {
        const uitkomst = v(a.rij, b.rij);
        if (uitkomst !== 0) return uitkomst;
      }
      return a.i - b.i;
    })
    .map(({ rij }) => rij);
}

/** Klik op een kolomkop (§5): toevoegen in eerste richting, dan wisselen, dan weghalen. */
export function klikOpKop<R, C extends Context>(sortering: Sortering, kolom: Kolom<R, C>, max = 5): Sortering {
  if (!sorteerbaar(kolom)) return sortering;
  const eerste = kolom.eersteRichting ?? 'op';
  const i = sortering.findIndex((s) => s.sleutel === kolom.sleutel);
  if (i < 0) {
    const nieuw = { sleutel: kolom.sleutel, richting: eerste };
    return sortering.length >= max ? [...sortering.slice(0, max - 1), nieuw] : [...sortering, nieuw];
  }
  if (sortering[i]!.richting === eerste) {
    return sortering.map((s, j) => (j === i ? { ...s, richting: eerste === 'op' ? 'af' : 'op' } : s));
  }
  return sortering.filter((_, j) => j !== i);
}
